/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { RequestError, useApi } from '../services';
import { ErrorOnDialog, TableNodeStyle } from '../components';
import { useBarragemContext } from './BarragemContext';
import { deepEqual, DiffFlow, diffFlowEntities } from '../core';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useUserContext } from './UserContext';

const MAX_HISTORY = 100;

export type FlowTipo = '' | 'Nível 1' | 'Nível 2' | 'Nível 3' | 'Nível de alerta';

export interface Flow {
  id: number;
  name: string;
  style?: React.CSSProperties;
  tipo: FlowTipo;
  nodes: Node[];
  edges: Edge[];
}

export interface Node {
  id: string;
  flowId: number;
  markdown?: string;
  style: (React.CSSProperties | TableNodeStyle) & { "parentId"?: string; };
  posX: number;
  posY: number;
  handles: Handle[];
}

export interface Edge {
  id: string;
  handleSourceId: string;
  handleTargetId: string;
  flowId: number;
  markdown?: string;
  style?: React.CSSProperties;
}

export interface Handle {
  id: string;
  nodeId: string;
  type: 'source' | 'target';
  position: 'left' | 'right' | 'top' | 'bottom';
  style?: React.CSSProperties;
}

interface ContextProps {
  flow?: Flow;
  setFlow: (flow: Flow) => void;
}

const FlowContext = createContext<ContextProps>({
  setFlow: () => {
    //
  }
});

interface HitoryProps {
  undo: () => DiffFlow | undefined;
  redo: () => DiffFlow | undefined;
  isUndable: boolean | undefined;
  isRedable: boolean | undefined;
}

const HistoryContext = createContext<HitoryProps>({
  undo: () => undefined,
  redo: () => undefined,
  isUndable: undefined,
  isRedable: undefined,
});

export const useFlowContext = () => {
  return useContext(FlowContext);
};

export const useHistoryContext = () => {
  return useContext(HistoryContext);
};

interface Props {
  children?: React.ReactNode;
  flowId: number;
}

export const FlowProvider: React.FC<Props> = ({ children, flowId }) => {
  const api = useApi();
  const { barragem } = useBarragemContext();
  const user = useUserContext();

  const [error, setError] = useState<RequestError>();
  const [flow, setFlow] = useState<Flow>();
  const [currentFlow, setCurrentFlow] = useState<Flow>();
  const [salvando, setSalvando] = useState<"Salvando" | "Salvo">();

  const isUndoRedo = useRef<boolean>(false);
  const history = useRef<DiffFlow[]>([]);
  const historyIndex = useRef(-1);
  const firstRun = useRef(true);
  const timerUpdate = useRef<NodeJS.Timer>();
  // const timerPrint = useRef<NodeJS.Timer>();

  useEffect(() => {
    if (!flow || !currentFlow || !barragem || "error" in barragem) return;
    if (!user || "error" in user || !user.admin) return;

    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    const cleanedFlow = cleanFlow(flow);
    const cleanedCurrentFlow = cleanFlow(currentFlow);

    if (deepEqual(cleanedFlow, cleanedCurrentFlow)) return;

    setSalvando("Salvando");

    const diff: DiffFlow = diffFlowEntities(cleanedCurrentFlow, cleanedFlow);

    // Atualizar o histórico dos dados
    if (timerUpdate.current) clearTimeout(timerUpdate.current);

    timerUpdate.current = setTimeout(async () => {

      if (isUndoRedo.current) {
        isUndoRedo.current = false;
        return;
      }

      // Salvando estado
      const newHistory = history.current.slice(0, historyIndex.current + 1);
      newHistory.push(diff);

      // Limita o tamanho do histórico
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      } else {
        historyIndex.current = newHistory.length - 1;
      }

      history.current = newHistory;

      /////////////////////////---Salvando os dados no DB
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nodePromises: Promise<any>[] = [];
        diff.nodes.updated.forEach(n => nodePromises.push(api.fluxogramas.updateNode(barragem.id, cleanedFlow.id, n.id, n)));
        diff.nodes.created.forEach(n => nodePromises.push(api.fluxogramas.addNode(barragem.id, cleanedFlow.id, n.posX, n.posY, n.markdown, n.style, n.id)));
        diff.nodes.deleted.forEach(n => nodePromises.push(api.fluxogramas.deleteNode(barragem.id, cleanedFlow.id, n.id)));

        if (nodePromises.length > 0) {
          const nodeResults = await Promise.all(nodePromises);
          const hasError = nodeResults.some(r => "error" in r);
          if (hasError) {
            setError(nodeResults.find(r => "error" in r) as RequestError);
            setSalvando(undefined);
            return;
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handlePromises: Promise<any>[] = [];
        diff.handles.updated.forEach(h => handlePromises.push(api.fluxogramas.updateHandle(barragem.id, cleanedFlow.id, h.id, h)));
        diff.handles.created.forEach(h => handlePromises.push(api.fluxogramas.addHandle(barragem.id, cleanedFlow.id, h.nodeId, h.type, h.position, h.style, h.id)));

        const deletedNodeIds = new Set(diff.nodes.deleted.map(n => n.id));
        const handlesToDelete = diff.handles.deleted.filter(
          h => !deletedNodeIds.has(h.nodeId)
        );
        handlesToDelete.forEach(h => handlePromises.push(api.fluxogramas.deleteHandle(barragem.id, cleanedFlow.id, h.id)));

        if (handlePromises.length > 0) {
          const handleResults = await Promise.all(handlePromises);
          const hasError = handleResults.some(r => "error" in r);
          if (hasError) {
            setError(handleResults.find(r => "error" in r) as RequestError);
            setSalvando(undefined);
            return;
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const edgePromises: Promise<any>[] = [];
        diff.edges.updated.forEach(e => edgePromises.push(api.fluxogramas.updateEdge(barragem.id, cleanedFlow.id, e.id, e)));
        diff.edges.created.forEach(e => edgePromises.push(api.fluxogramas.addEdge(barragem.id, cleanedFlow.id, e.handleSourceId, e.handleTargetId, e.markdown, e.style, e.id)));

        const deletedHandleIds = new Set(diff.handles.deleted.map(h => h.id));
        const edgesToDelete = diff.edges.deleted.filter(
          e => !deletedHandleIds.has(e.handleSourceId) && !deletedHandleIds.has(e.handleTargetId)
        );
        edgesToDelete.forEach(e => edgePromises.push(api.fluxogramas.deleteEdge(barragem.id, cleanedFlow.id, e.id)));

        if (edgePromises.length > 0) {
          const edgeResults = await Promise.all(edgePromises);
          const hasError = edgeResults.some(r => "error" in r);
          if (hasError) {
            setError(edgeResults.find(r => "error" in r) as RequestError);
            setSalvando(undefined);
            return;
          }
        }

        setCurrentFlow(cleanedFlow);
        setSalvando("Salvo");

      } catch (err) {
        setError({ error: 'unexpected_failure', message: "Erro de rede ou servidor durante a atualização do fluxo." });
        setSalvando(undefined);
      }
    }, 100);

    // if (timerPrint.current) clearTimeout(timerPrint.current);
    // timerPrint.current = setInterval(() => {
    //   console.log(historyIndex.current, history.current);
    // }, 1000);

    // return () => {
    //   if (timerPrint.current) clearTimeout(timerPrint.current);
    // };

  }, [flow]);

  useEffect(() => {
    if (!barragem || "error" in barragem || !flowId) return;

    api.fluxogramas.getFlow(barragem.id, flowId).then((value) => {
      if ("error" in value) {
        setError(value);
      } else {
        setFlow(value);
        setCurrentFlow(value);
      }
    });
  }, [barragem, flowId]);

  const handleUpdateByHistoryIndex = (index: number): DiffFlow => {
    isUndoRedo.current = true;
    historyIndex.current = index;
    const value = history.current[index];
    return value;
  };

  // Desfazer (Ctrl+Z)
  const undo = () => {
    if (historyIndex.current < 0) return;
    const changes = handleUpdateByHistoryIndex(historyIndex.current);
    historyIndex.current = historyIndex.current - 1;
    return changes;
  };

  // Refazer (Ctrl+Y)
  const redo = () => {
    if (historyIndex.current >= history.current.length - 1) return;
    const newIndex = historyIndex.current + 1;
    const changes = handleUpdateByHistoryIndex(newIndex);

    let c: Node[] | Edge[] | Handle[] = changes.edges.created;
    changes.edges.created = changes.edges.deleted;
    changes.edges.deleted = c;

    c = changes.nodes.created;
    changes.nodes.created = changes.nodes.deleted;
    changes.nodes.deleted = c;

    c = changes.handles.created;
    changes.handles.created = changes.handles.deleted;
    changes.handles.deleted = c;

    return changes;
  };

  return (
    <>
      {salvando && <Snackbar
        open={Boolean(salvando)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setSalvando(undefined)}
        autoHideDuration={salvando !== "Salvando" ? 2000 : undefined}
      >
        <Alert
          severity={salvando === "Salvando" ? 'warning' : 'success'}
          icon={
            salvando === "Salvando" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CheckCircleIcon fontSize="inherit" />
            )
          }
          sx={{
            width: "136px",
            backgroundColor: salvando === "Salvando" ? '#ffeb3b' : '#4caf50',
            color: salvando === "Salvando" ? 'rgba(0, 0, 0, 0.87)' : '#fff',
            '& .MuiAlert-icon': {
              color: salvando === "Salvando" ? 'rgba(0, 0, 0, 0.87)' : '#fff',
            }
          }}
        >
          {salvando}
        </Alert>
      </Snackbar>}

      {error && (<ErrorOnDialog open={true} {...error} onClose={() => setError(undefined)} />)}

      <FlowContext.Provider value={{ flow: flow, setFlow }}>
        <HistoryContext.Provider value={{
          undo: undo,
          redo: redo,
          isUndable: historyIndex.current > 0,
          isRedable: historyIndex.current < history.current.length,
        }}>
          {children}
        </HistoryContext.Provider>
      </FlowContext.Provider>
    </>
  );
};

function cleanFlow(flow: Flow): Flow {
  const cleanNode = (node: any): Node => {
    const dados = {
      id: node.id,
      flowId: node.flowId,
      markdown: node.markdown,
      posX: node.posX,
      posY: node.posY,
      style: { ...node.style || {}, parentId: node.style.parentId },
      handles: node.handles.map((handle: any) => ({
        id: handle.id,
        nodeId: handle.nodeId,
        type: handle.type,
        position: handle.position,
      })),
    };
    return dados;
  };

  const cleanEdge = (edge: any): Edge => ({
    id: edge.id,
    flowId: edge.flowId,
    handleSourceId: edge.handleSourceId,
    handleTargetId: edge.handleTargetId,
    markdown: edge.markdown,
    style: edge.style || {},
  });

  const dados = {
    id: flow.id,
    name: flow.name,
    style: flow.style || {},
    tipo: flow.tipo,
    nodes: flow.nodes.map(cleanNode),
    edges: flow.edges.map(cleanEdge),
  };

  return dados;
}
